import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import { useEffect, useMemo } from 'react';

import AuthExperience from '../../components/_organisms/Auth/AuthExperience';
import MainLoader from '../../components/Loader';
import useRouterMatch from '../../hooks/useRouterMatch';
import useSignUp from '../../hooks/useSignup';
import { clearAuthSessionCookie } from '../../utils/clearAuthSessionCookie';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';
import { isSafeRedirect } from '../../utils/navigator';
import { readPostAuthRedirect, savePostAuthRedirect } from '../../utils/postAuthRedirect';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../utils/react-query-utils';
import { resolveNewAuth } from '../../utils/resolveNewAuth';
import { provisionFlags } from '../../utils/ServerSideFeatureFlagUtils';

export default function Signup({ newAuth }: { newAuth?: boolean }) {
  const router = useRouter();
  const { signUp } = useSignUp();
  const { tenantId } = useRouterMatch();
  const session = useSession();
  // An errored session (provisioning failure, no idToken) is not a real login —
  // treat it as logged-out so the form still renders ("Try again" from /auth/error).
  const isLoggedIn = session.status === 'authenticated' && !session.data?.error;

  const safeQueryCallback = useMemo(() => {
    const queryCallback =
      (router.query.callbackUrl as string) ||
      (router.query.callback as string) ||
      (router.query.redirect as string);
    return isSafeRedirect(queryCallback) ? queryCallback : null;
  }, [router.query.callbackUrl, router.query.callback, router.query.redirect]);

  const finalCallbackUrl = useMemo(
    () => safeQueryCallback ?? readPostAuthRedirect() ?? '/',
    [safeQueryCallback],
  );

  // Persist an explicit callback/redirect query so it survives the OAuth
  // round-trip. The write is a side effect (belongs in an effect, not useMemo)
  // and only runs when one was provided — otherwise a bare /signup visit would
  // overwrite a previously stored destination with the '/' default.
  useEffect(() => {
    if (safeQueryCallback) savePostAuthRedirect(safeQueryCallback);
  }, [safeQueryCallback]);

  // An authenticated user has no reason to be on the signup page — send them to
  // the destination (mirrors /login; the GSSP covers the SSR case). Applies to
  // both the new and legacy experiences.
  useEffect(() => {
    if (isLoggedIn) router.push(finalCallbackUrl);
  }, [isLoggedIn, router, finalCallbackUrl]);

  useEffect(() => {
    if (newAuth) return; // new experience renders the form in-place
    // Wait for the session to resolve, and don't push an already-signed-in user
    // into B2C signup (the effect above redirects them) — mirrors /login.
    if (session.status === 'loading' || isLoggedIn) return;
    // Legacy: skip the signup form and go directly to Azure B2C.
    signUp({
      callbackUrl: finalCallbackUrl,
      authParams: {
        ui_locales: 'en',
        accountType: 'teacher',
        tenantName: '',
        schoolRole: '',
        subjects: '',
        grades: '',
        primaryUse: '',
        otherRole: '',
        otherSubjects: '',
        otherGrades: '',
        otherPrimaryUse: '',
        v3Properties: JSON.stringify({
          organizationId: undefined,
          organizationGeoLocation: {
            country: '',
            region: '',
            city: '',
          },
          intendedUses: '',
          signUpOrigin: '',
          otherIntendedUse: '',
          otherSignUpOrigin: '',
        }),
      },
    });
  }, [signUp, finalCallbackUrl, tenantId, newAuth, session.status, isLoggedIn]);

  // Render the form only once the session has resolved (not while `loading`) so a
  // returning user about to resolve as authenticated doesn't see a flash of it.
  if (newAuth && session.status !== 'loading' && !isLoggedIn) {
    return (
      <>
        <Head>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
        <AuthExperience initialMode="signup" callbackUrl={finalCallbackUrl} />
      </>
    );
  }

  // Show loading state while redirecting to Azure B2C
  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      <MainLoader />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'no-store, no-cache, must-revalidate, proxy-revalidate',
  );

  const rawSession = await getSession({ req: context.req });
  // An errored session (provisioning failure) must NOT reach the client: clear its
  // cookie and treat it as logged-out, or the global BackendServiceProvider signs
  // the user out to /auth/error and blocks retry here. Mirrors /auth/error.
  if (rawSession?.error) clearAuthSessionCookie(context.res);
  const session = rawSession?.error ? null : rawSession;
  const queryClient = new QueryClient();
  const flags = await provisionFlags(context, queryClient, session);
  const flagOn = !!flags.unleash.toggles?.find(t => t.name === 'US_AUTH')?.enabled;
  // Non-production QA override so staging can exercise the new experience
  // before the Unleash toggle is created (?newauth=1).
  const newAuth = resolveNewAuth(flagOn, context);

  // An already-authenticated user has no reason to see the signup form — send them
  // to their requested destination (mirrors the login page), honoring a safe
  // callback/redirect query and defaulting to home. Applies to both experiences,
  // so legacy signup doesn't push a signed-in user into Azure B2C.
  if (session) {
    const requested =
      (context.query.callbackUrl as string) ||
      (context.query.callback as string) ||
      (context.query.redirect as string);
    const destination = isSafeRedirect(requested) ? requested : '/';
    return { redirect: { permanent: false, destination } };
  }

  return {
    props: {
      ...flags,
      newAuth,
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'app' })),
      // No session prop here: an authenticated (non-errored) session already
      // redirected above, and an errored one was cleared — so this path is
      // unauthenticated.
    },
  };
};
